import type {
    BoardListWithImageOnlyPinsFragment,
    DesignBoardPin,
    Scalars,
} from 'generated/graphql';
import type { ShorthandCollection, ImageProps } from '@archipro-design/aria';
import {
    DESIGN_BOARD_HOME_LINK,
    LOGO_PLACEHOLDER_BG_COLOR,
    LOGO_TRANSPARENT_BG_COLOR,
} from '~/modules/design-board/config';
import { getValidEdgeNodes } from '~/modules/design-board/util';

import { UPLOAD_PLACEHOLDER_BG_IMG } from '~/modules/design-board/config';
export interface DesignBoardPreviewProps {
    id: number;
    label: string;
    pins: number;
    link: string;
    images: ShorthandCollection<ImageProps>;
    fileExtensions?: string[];
}

interface DesignBoardPinWithImages extends DesignBoardPin {
    Target: {
        ID: Scalars['Int'];
        LibraryImageFile?: {
            PhotoTile: string;
        };
    };
}

export const mapMemberBoardListPinData = (
    assetsBaseUrl: string,
    designBoards: BoardListWithImageOnlyPinsFragment[] = [],
    pinsVisible = 3,
    tilesVisible = 0
): DesignBoardPreviewProps[] => {
    const designBoardPreviews: DesignBoardPreviewProps[] = [];

    designBoards.forEach((designBoard) => {
        if (tilesVisible > 0 && designBoardPreviews.length >= tilesVisible) {
            return;
        }

        const boardLink = `${DESIGN_BOARD_HOME_LINK}/${designBoard.URLSegment}`;

        const pinNodes = getValidEdgeNodes<DesignBoardPin[]>(
            designBoard.ImageOnlyPins.edges
        );

        let pinCount = 0;
        const pinImages: ShorthandCollection<ImageProps> = [];
        const fileExtensions: string[] = [];

        pinNodes.forEach((pin) => {
            if (pinCount > pinsVisible) {
                return;
            }

            const fileExtension = getFileExtension(pin);
            if (fileExtension) {
                fileExtensions.push(fileExtension);
            }
            let possibleImage = fileExtension
                ? `${assetsBaseUrl}${UPLOAD_PLACEHOLDER_BG_IMG}`
                : getImageUrl(pin);

            const objectFit =
                'LogoBackgroundColor' in pin.Target ? 'contain' : 'cover';
            const bgColor =
                'LogoBackgroundColor' in pin.Target
                    ? pin.Target.LogoBackgroundColor ??
                      LOGO_PLACEHOLDER_BG_COLOR
                    : LOGO_TRANSPARENT_BG_COLOR;
            const mixBlendMode =
                'LogoBackgroundColor' in pin.Target ? 'normal' : 'multiply';

            if (possibleImage) {
                pinImages.push({
                    src: possibleImage,
                    width: 400,
                    height: 400,
                    alt: pin.Title,
                    className: fileExtension ? `pin-tile-${fileExtension}` : '',
                    objectFit,
                    placeholderColor: 'light',
                    variables: {
                        placeholderBackgroundColorLight: bgColor,
                        mixBlendMode: mixBlendMode,
                    },
                });
                pinCount++;
            }
        });

        designBoardPreviews.push({
            id: designBoard.ID,
            label: designBoard.Title,
            pins: designBoard.PinsCount ? designBoard.PinsCount : 0,
            link: boardLink,
            images: pinImages,
            fileExtensions,
        });
    });

    // fill blank tiles
    let blankTiles = 0;
    while (designBoardPreviews.length < tilesVisible) {
        designBoardPreviews.unshift({
            id: blankTiles++,
            label: '',
            pins: -1,
            link: '',
            images: [],
            fileExtensions: [],
        });
    }

    return designBoardPreviews;
};

const getImageUrl = (pin: Partial<DesignBoardPinWithImages>) => {
    if (pin?.Target?.LibraryImageFile) {
        return pin.Target.LibraryImageFile.PhotoTile;
    }
    return pin.PhotoTile;
};

const getFileExtension = (pin: DesignBoardPin) => {
    const isFile =
        '__typename' in pin.Target && pin.Target.__typename === 'MemberUpload';

    if (!isFile) return '';

    const filename = pin.Title || '';
    const fileExtension = filename.split('.').pop();
    const isUploadedImage = fileExtension?.match(/(jpg|jpeg|png|gif|webp)$/i);

    return isUploadedImage ? '' : fileExtension;
};
